// src/api/index.ts
import axios from 'axios';
import {NavigateFunction} from 'react-router-dom';
import {getAuthToken} from '../../src/utils/auth';
import config from '../config';
import {ERROR_STATUS_CODE} from '../constants/constants';
import {
  FC_API_RETRY_POLLING_DELAY,
  FC_BASE_URL,
  FC_MAX_RETRY_CYCLE,
  fcDelay,
  isFCSuccess,
} from '../utils/fcUploadFileUtils';
import {calculateResponseTime} from './utils';
import {leaveAppIfPlatformMaintenance} from '../utils/maintenanceLeaveApp';

/**
 * Flag to determine whether the spa will send a bearer token, or to rely on the newer cookies based process.
 */
const usingBffTokenMediator =
  config.API_AUTH_MODE == 'bff-token-mediator' ? true : false;

let navigateFn: NavigateFunction | undefined;

export const setNavigate = (fn?: NavigateFunction) => {
  navigateFn = fn;
};

export const useApiBaseUrl = (relative: string) => {
  let url = config.API_BASE_URL;
  if (!url.endsWith('/')) {
    url += '/';
  }

  if (relative.startsWith('/')) {
    url += relative.substring(1);
  } else {
    url += relative;
  }

  return url;
};

const apiClient = axios.create({
  baseURL: `${config.API_BASE_URL}`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor to handle authorization
apiClient.interceptors.request.use(
  (config) => {
    if (!usingBffTokenMediator) {
      config.headers['Authorization'] = getAuthToken();
    }
    config.headers['x-start-time'] = performance.now().toString();
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => {
    if (typeof response?.data === 'object')
      response.data.responseTime = calculateResponseTime(response.config);
    return response;
  },
  async (error) => {
    if (typeof error === 'object')
      error.responseTime = calculateResponseTime(error.config);

    const status = error?.response?.status;
    const requestUrl = error?.request?.responseURL;
    if (
      status === ERROR_STATUS_CODE.STATUS_403 &&
      !requestUrl?.includes('inprogressevidencesources')
    ) {
      if (typeof navigateFn === 'function') {
        navigateFn('/secure-page', {replace: true});
      } else {
        window.location.replace('/secure-page');
      }
    }

    await leaveAppIfUnavailable(status, error?.config?.url || requestUrl);
    return Promise.reject(error);
  }
);

export const apiMwClient = axios.create({
  baseURL: `${config.API_MW_BASE_URL}`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const fileCatalystClient = axios.create({
  baseURL: `${FC_BASE_URL}`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor to handle authorization
apiMwClient.interceptors.request.use(
  (config) => {
    if (!usingBffTokenMediator) {
      config.headers['Authorization'] = getAuthToken();
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiMwClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    await leaveAppIfUnavailable(
      status,
      error?.config?.url || error?.request?.responseURL
    );
    return Promise.reject(error);
  }
);

fileCatalystClient.interceptors.response.use(
  async (response) => {
    const config = response?.config ?? {};
    const {shouldRetry = false, onRetry, onRetryCompleted} = config;

    if (!isFCSuccess(response) && shouldRetry) {
      const error = new Error('Non-success status');
      Object.assign(error, {config, response});

      config.__cycleCount = config.__cycleCount || 0;

      const maxCycles = config?.maxCycles ?? FC_MAX_RETRY_CYCLE;
      const cycleDelay = config?.cycleDelay ?? FC_API_RETRY_POLLING_DELAY;

      config.__cycleCount += 1;

      onRetry?.({
        attempt: config.__cycleCount,
        delay: cycleDelay,
        status: response?.status || 'NO_RESPONSE',
        error,
      });

      if (config.__cycleCount >= maxCycles) {
        onRetryCompleted?.();
        throw error;
      }

      await fcDelay(cycleDelay);
      return fileCatalystClient(config);
    }

    return response;
  },

  async (error) => {
    const config = error.config;
    const {shouldRetry = false, onRetry, onRetryCompleted} = config;

    if (!shouldRetry) {
      throw error;
    }

    config.__cycleCount = config.__cycleCount || 0;

    const maxCycles = config?.maxCycles ?? FC_MAX_RETRY_CYCLE;
    const cycleDelay = config?.cycleDelay ?? FC_API_RETRY_POLLING_DELAY;

    config.__cycleCount += 1;

    onRetry?.({
      attempt: config.__cycleCount,
      delay: cycleDelay,
      status: error?.response?.status || 'NO_RESPONSE',
      error,
    });

    if (config.__cycleCount >= maxCycles) {
      onRetryCompleted?.(error);
      throw error;
    }

    await fcDelay(cycleDelay);
    return fileCatalystClient(config);
  }
);

const auroraIsUnavailable = (statusCode: any) => {
  const badGateway = statusCode === ERROR_STATUS_CODE.STATUS_502;
  const serviceUnavailable = statusCode === ERROR_STATUS_CODE.STATUS_503;
  return badGateway || serviceUnavailable;
};

const isLogoutRequest = (url: string) => /account\/logout/i.test(url);

const leaveAppIfUnavailable = async (status: any, requestUrl?: string) => {
  if (!auroraIsUnavailable(status)) {
    return;
  }

  const url = String(requestUrl || '');
  if (isLogoutRequest(url)) {
    return;
  }

  await leaveAppIfPlatformMaintenance();
};

export default apiClient;
